import { useCallback, useEffect, useState } from "react";
import { Check, ChevronDown, Eye, LogOut, Pencil, Plus, RotateCcw, UserPlus } from "lucide-react";
import { BrandLockup } from "@/components/landing/LogoMark";
import { platformClient, MODULES, formatPlatformError, isPasswordStrong, PASSWORD_POLICY_MSG } from "./platformApi";
import { AdminBillingTab } from "./AdminBillingTab";
import { AdminBooksTab } from "./AdminBooksTab";
import { AdminPipelineTab } from "./AdminPipelineTab";
import { AdminOnboardingTab } from "./AdminOnboardingTab";
import { AdminProposalsTab } from "./AdminProposalsTab";

const ADMIN_TABS = [
    { id: "klanten", label: "Klanten" },
    { id: "verkoop", label: "Verkoop-pijplijn" },
    { id: "facturatie", label: "Abonnementen & facturatie" },
    { id: "onboarding", label: "Onboarding" },
    { id: "voorstellen", label: "Voorstellen" },
    { id: "boekhouding", label: "Mijn boekhouding" },
];

const Toggle = ({ on, testid, onClick }) => (
    <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        className={`relative h-6 w-11 shrink-0 rounded-full border transition-colors ${
            on ? "border-gold bg-gold/20" : "border-white/20 bg-white/5"
        }`}
        aria-pressed={on}
    >
        <span
            className={`absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full transition-all ${
                on ? "left-6 bg-gold" : "left-1 bg-ash"
            }`}
        />
    </button>
);

const inputCls =
    "mt-2 w-full border-b border-white/20 bg-transparent pb-2.5 text-sm text-bone placeholder:text-ash/50 transition-colors focus:border-gold focus:outline-none";
const labelCls = "text-[10px] uppercase tracking-[0.25em] text-ash";

export const PlatformAdmin = ({ user, onLogout, onRefresh }) => {
    const [tab, setTab] = useState("klanten");
    const [tenants, setTenants] = useState([]);
    const [tenantName, setTenantName] = useState("");
    const [tenantEmail, setTenantEmail] = useState("");
    const [error, setError] = useState("");
    const [expanded, setExpanded] = useState(null);
    const [users, setUsers] = useState({});
    const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "member" });
    const [renaming, setRenaming] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const [details, setDetails] = useState({});

    const DETAIL_FIELDS = [
        ["address", "Adres"],
        ["postal_city", "Postcode & plaats"],
        ["kvk", "KvK-nummer"],
        ["btw_number", "BTW-nummer"],
        ["iban", "IBAN"],
        ["phone", "Telefoon"],
        ["email", "E-mail"],
    ];

    const load = useCallback(async () => {
        try {
            const { data } = await platformClient.get("/tenants");
            setTenants(data);
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const createTenant = async (e) => {
        e.preventDefault();
        setError("");
        try {
            await platformClient.post("/tenants", { name: tenantName, contact_email: tenantEmail });
            setTenantName("");
            setTenantEmail("");
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const toggleModule = async (tenant, moduleId) => {
        const modules = { ...tenant.modules, [moduleId]: !tenant.modules?.[moduleId] };
        setTenants((list) => list.map((t) => (t.id === tenant.id ? { ...t, modules } : t)));
        try {
            await platformClient.patch(`/tenants/${tenant.id}`, { modules });
        } catch {
            load();
        }
    };

    const saveRename = async (t) => {
        const name = renameValue.trim();
        setRenaming(null);
        if (!name || name === t.name) return;
        setTenants((list) => list.map((x) => (x.id === t.id ? { ...x, name } : x)));
        try {
            await platformClient.patch(`/tenants/${t.id}`, { name });
        } catch {
            load();
        }
    };

    const resetDemo = async (t) => {
        if (!window.confirm("Demo-data terugzetten naar de nette beginstand?")) return;
        try {
            await platformClient.post(`/admin/demo-reset/${t.id}`);
            load();
        } catch {}
    };

    const loadUsers = async (tenantId) => {
        try {
            const { data } = await platformClient.get(`/tenants/${tenantId}/users`);
            setUsers((m) => ({ ...m, [tenantId]: data }));
        } catch {}
    };

    const expandTenant = (tenantId) => {
        const next = expanded === tenantId ? null : tenantId;
        setExpanded(next);
        if (next) {
            loadUsers(next);
            const t = tenants.find((x) => x.id === tenantId);
            setDetails((m) => ({ ...m, [tenantId]: { ...(t?.company_details || {}) } }));
        }
    };

    const saveDetails = async (t) => {
        try {
            await platformClient.patch(`/tenants/${t.id}`, { company_details: details[t.id] || {} });
            load();
        } catch {}
    };

    const createUser = async (e, tenantId) => {
        e.preventDefault();
        setError("");
        if (!isPasswordStrong(newUser.password)) {
            setError(PASSWORD_POLICY_MSG);
            return;
        }
        try {
            await platformClient.post(`/tenants/${tenantId}/users`, newUser);
            setNewUser({ name: "", email: "", password: "", role: "member" });
            loadUsers(tenantId);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    return (
        <div data-testid="platform-admin" className="min-h-screen bg-ink text-bone">
            <header className="sticky top-0 z-10 border-b border-white/10 bg-black/60 backdrop-blur-xl">
                <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-4">
                    <div className="flex items-center gap-5">
                        <BrandLockup emblemClass="h-9 w-9" textClass="text-base" align="start" />
                        <span className="border-l border-white/15 pl-5 text-[11px] uppercase tracking-[0.3em] text-ash">
                            Platform — Beheer
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="hidden text-xs text-ash sm:block">{user.email}</span>
                        <button
                            data-testid="platform-admin-logout"
                            onClick={onLogout}
                            className="flex items-center gap-2 border border-white/15 px-4 py-2.5 text-xs uppercase tracking-[0.2em] text-ash transition-colors hover:border-gold/60 hover:text-gold"
                        >
                            <LogOut className="h-3.5 w-3.5" /> Uitloggen
                        </button>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-[1200px] px-6 py-10">
                <div data-testid="admin-tabs" className="mb-8 flex flex-wrap gap-2">
                    {ADMIN_TABS.map((t) => (
                        <button
                            key={t.id}
                            data-testid={`admin-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`border px-4 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors ${
                                tab === t.id
                                    ? "border-gold bg-gold/10 text-gold"
                                    : "border-white/10 text-ash hover:border-gold/40 hover:text-bone"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                {tab === "verkoop" ? (
                    <AdminPipelineTab />
                ) : tab === "onboarding" ? (
                    <AdminOnboardingTab />
                ) : tab === "facturatie" ? (
                    <AdminBillingTab tenants={tenants} />
                ) : tab === "voorstellen" ? (
                    <AdminProposalsTab />
                ) : tab === "boekhouding" ? (
                    <AdminBooksTab />
                ) : (
                <>
                <div className="border border-white/10 bg-coal p-6 md:p-8">
                    <h2 className="font-display text-2xl tracking-tight">Nieuwe klant</h2>
                    <form onSubmit={createTenant} className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
                        <label className="block">
                            <span className={labelCls}>Bedrijfsnaam</span>
                            <input
                                data-testid="tenant-create-name-input"
                                value={tenantName}
                                onChange={(e) => setTenantName(e.target.value)}
                                required
                                maxLength={120}
                                className={inputCls}
                            />
                        </label>
                        <label className="block">
                            <span className={labelCls}>Contact e-mail</span>
                            <input
                                data-testid="tenant-create-email-input"
                                type="email"
                                value={tenantEmail}
                                onChange={(e) => setTenantEmail(e.target.value)}
                                required
                                maxLength={200}
                                className={inputCls}
                            />
                        </label>
                        <div className="flex items-end">
                            <button
                                data-testid="tenant-create-submit-button"
                                type="submit"
                                className="flex w-full items-center justify-center gap-2 bg-gold px-6 py-3 text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-bone"
                            >
                                <Plus className="h-4 w-4" /> Klant toevoegen
                            </button>
                        </div>
                    </form>
                    {error ? (
                        <p data-testid="platform-admin-error" className="mt-4 text-sm text-red-400">
                            {error}
                        </p>
                    ) : null}
                </div>

                <h2 className="mt-12 font-display text-2xl tracking-tight">
                    Klanten ({tenants.length})
                </h2>
                {tenants.length === 0 ? (
                    <p data-testid="platform-admin-empty" className="mt-6 text-sm text-ash">
                        Nog geen klanten. Voeg hierboven je eerste klant toe.
                    </p>
                ) : (
                    <div className="mt-6 space-y-4">
                        {tenants.map((t) => (
                            <div
                                key={t.id}
                                data-testid={`tenant-card-${t.id}`}
                                className="border border-white/10 bg-coal"
                            >
                                <div className="flex flex-wrap items-center justify-between gap-4 p-6">
                                    <div>
                                        {renaming === t.id ? (
                                            <span className="flex items-center gap-2">
                                                <input
                                                    data-testid={`tenant-rename-input-${t.id}`}
                                                    value={renameValue}
                                                    onChange={(e) => setRenameValue(e.target.value)}
                                                    onKeyDown={(e) => e.key === "Enter" && saveRename(t)}
                                                    autoFocus
                                                    className="border-b border-gold bg-transparent pb-1 font-display text-xl text-bone focus:outline-none"
                                                />
                                                <button
                                                    data-testid={`tenant-rename-save-${t.id}`}
                                                    onClick={() => saveRename(t)}
                                                    aria-label="Naam opslaan"
                                                    className="text-gold transition-colors hover:text-bone"
                                                >
                                                    <Check className="h-4 w-4" />
                                                </button>
                                            </span>
                                        ) : (
                                            <p className="font-display text-xl">
                                                {t.name}
                                                <button
                                                    data-testid={`tenant-rename-${t.id}`}
                                                    onClick={() => {
                                                        setRenaming(t.id);
                                                        setRenameValue(t.name);
                                                    }}
                                                    aria-label="Naam wijzigen"
                                                    className="ml-2 inline text-ash/50 transition-colors hover:text-gold"
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </button>
                                            </p>
                                        )}
                                        <p className="mt-1 text-xs text-ash">
                                            {t.contact_email} · {t.user_count ?? 0} gebruiker(s)
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        {t.is_demo ? (
                                            <button
                                                data-testid={`tenant-demo-reset-${t.id}`}
                                                onClick={() => resetDemo(t)}
                                                className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.15em] text-ash transition-colors hover:border-gold/60 hover:text-gold"
                                            >
                                                <RotateCcw className="h-3.5 w-3.5" /> Reset demo
                                            </button>
                                        ) : null}
                                        <button
                                            data-testid={`tenant-view-${t.id}`}
                                            onClick={async () => {
                                                await platformClient.post(`/admin/impersonate/${t.id}`);
                                                onRefresh();
                                            }}
                                            className="flex items-center gap-2 border border-gold/50 px-4 py-2 text-xs uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black"
                                        >
                                            <Eye className="h-3.5 w-3.5" /> Bekijk
                                        </button>
                                        <button
                                            data-testid={`tenant-expand-${t.id}`}
                                            onClick={() => expandTenant(t.id)}
                                            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ash transition-colors hover:text-gold"
                                        >
                                            Gebruikers
                                            <ChevronDown
                                                className={`h-4 w-4 transition-transform ${
                                                    expanded === t.id ? "rotate-180" : ""
                                                }`}
                                            />
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 border-t border-white/10 px-6 py-5 md:grid-cols-4">
                                    {MODULES.map((m) => (
                                        <div key={m.id} className="flex items-center justify-between gap-3">
                                            <span className="text-xs uppercase tracking-[0.15em] text-ash">
                                                {m.label}
                                            </span>
                                            <Toggle
                                                testid={`module-toggle-${t.id}-${m.id}`}
                                                on={!!t.modules?.[m.id]}
                                                onClick={() => toggleModule(t, m.id)}
                                            />
                                        </div>
                                    ))}
                                </div>
                                {expanded === t.id ? (
                                    <div className="border-t border-white/10 px-6 py-6">
                                        <div className="space-y-3">
                                            {(users[t.id] || []).map((u) => (
                                                <div
                                                    key={u.id}
                                                    data-testid={`tenant-user-${u.id}`}
                                                    className="flex flex-wrap items-center justify-between gap-2 border border-white/10 bg-ink px-4 py-3"
                                                >
                                                    <span className="text-sm text-bone">{u.name}</span>
                                                    <span className="text-xs text-ash">{u.email}</span>
                                                    <span className="text-[10px] uppercase tracking-[0.2em] text-gold">
                                                        {u.role === "owner" ? "Beheerder" : "Medewerker"}
                                                    </span>
                                                </div>
                                            ))}
                                            {(users[t.id] || []).length === 0 ? (
                                                <p className="text-sm text-ash">Nog geen gebruikers.</p>
                                            ) : null}
                                        </div>
                                        <form
                                            onSubmit={(e) => createUser(e, t.id)}
                                            className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-5"
                                        >
                                            <label className="block">
                                                <span className={labelCls}>Naam</span>
                                                <input
                                                    data-testid={`user-create-name-${t.id}`}
                                                    value={newUser.name}
                                                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                                                    required
                                                    className={inputCls}
                                                />
                                            </label>
                                            <label className="block">
                                                <span className={labelCls}>E-mail</span>
                                                <input
                                                    data-testid={`user-create-email-${t.id}`}
                                                    type="email"
                                                    value={newUser.email}
                                                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                                    required
                                                    className={inputCls}
                                                />
                                            </label>
                                            <label className="block">
                                                <span className={labelCls}>Wachtwoord</span>
                                                <input
                                                    data-testid={`user-create-password-${t.id}`}
                                                    type="text"
                                                    value={newUser.password}
                                                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                                    required
                                                    minLength={10}
                                                    className={inputCls}
                                                />
                                                <span className="mt-1 block text-[10px] text-ash/60">{PASSWORD_POLICY_MSG}</span>
                                            </label>
                                            <label className="block">
                                                <span className={labelCls}>Rol</span>
                                                <select
                                                    data-testid={`user-create-role-${t.id}`}
                                                    value={newUser.role}
                                                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                                                    className="mt-2 w-full border-b border-white/20 bg-ink pb-2.5 text-sm text-bone focus:border-gold focus:outline-none"
                                                >
                                                    <option value="member">Medewerker</option>
                                                    <option value="owner">Beheerder</option>
                                                </select>
                                            </label>
                                            <div className="flex items-end">
                                                <button
                                                    data-testid={`user-create-submit-${t.id}`}
                                                    type="submit"
                                                    className="flex w-full items-center justify-center gap-2 border border-gold/50 px-4 py-3 text-xs uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black"
                                                >
                                                    <UserPlus className="h-4 w-4" /> Toevoegen
                                                </button>
                                            </div>
                                        </form>
                                        <div className="mt-8 border-t border-white/10 pt-6">
                                            <p className={labelCls}>Bedrijfsgegevens (verschijnen op offertes &amp; facturen)</p>
                                            <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-3">
                                                {DETAIL_FIELDS.map(([key, label]) => (
                                                    <label key={key} className="block">
                                                        <span className={labelCls}>{label}</span>
                                                        <input
                                                            data-testid={`tenant-details-${key}-${t.id}`}
                                                            value={(details[t.id] || {})[key] || ""}
                                                            onChange={(e) =>
                                                                setDetails((m) => ({
                                                                    ...m,
                                                                    [t.id]: { ...(m[t.id] || {}), [key]: e.target.value },
                                                                }))
                                                            }
                                                            className={inputCls}
                                                        />
                                                    </label>
                                                ))}
                                            </div>
                                            <button
                                                data-testid={`tenant-details-save-${t.id}`}
                                                onClick={() => saveDetails(t)}
                                                className="mt-5 border border-gold/50 px-6 py-2.5 text-xs uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black"
                                            >
                                                Gegevens opslaan
                                            </button>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                )}
                </>
                )}
            </div>
        </div>
    );
};
